import { useMemo, useState } from "react";
import { useTenantOrganization } from "../../../lib/api";
import {
  dealerRows,
  indexOrg,
  organizationTree,
  regionNamesForDealer,
} from "../../../lib/organization";
import { useDealerEditor, useRegionEditor, useWorkshopEditor } from "../../SuperAdmin/useOrgEditors";
import { Icon } from "../../../components/msd/Icon";
import {
  Btn,
  CARD,
  DISP,
  Dot,
  Empty,
  Field,
  INPUT,
  Collapse,
  Page,
  PageHead,
  SearchBox,
  Seg2,
  SelectBox,
  Sheet,
  Switch,
  cx,
} from "../../../components/msd/ui";
import WorkshopSheet from "./WorkshopSheet";
import { ConfirmDelete, FieldGroup, Gico, OrgError, OrgLoading, RemoveBtn, TG_LIST, TG_ROW, plural } from "./kit";

// Icon tiles: a soft brand tint with a brand-blue glyph, not a solid block.
const TINT = "var(--royal-soft)";

function Stat({ n, label }) {
  return (
    <div>
      <b className="block text-[22px] font-bold leading-[1.1] tracking-[-0.02em]">{n}</b>
      <span className="text-[12px] text-msd-muted">{label}</span>
    </div>
  );
}

/**
 * Organization: regions, their dealers and those dealers' workshops on one
 * page — what used to be the Regions, Dealers and Workshops pages. Each part
 * keeps its own page's permission: a login sees only the parts it could open
 * before, and changes only the parts it could change.
 *
 * @param access  useOrgAccess() — { regions, dealers, workshops } × { see, edit }
 */
export default function OrganizationPage({ access }) {
  const { data, isLoading, isError, error, refetch } = useTenantOrganization(null);
  const [filters, setFilters] = useState({ query: "", region: "all", dealer: "all", status: "all" });
  const [collapsed, setCollapsed] = useState(() => new Set());
  // The workshop whose sheet is open.
  const [openShop, setOpenShop] = useState(null);

  const ix = useMemo(() => indexOrg(data), [data]);
  const see = useMemo(
    () => ({ regions: access.regions.see, dealers: access.dealers.see, workshops: access.workshops.see }),
    [access.regions.see, access.dealers.see, access.workshops.see],
  );
  const all = useMemo(() => organizationTree(ix, {}, see), [ix, see]);
  const tree = useMemo(() => organizationTree(ix, filters, see), [ix, filters, see]);
  const dealerRowById = useMemo(() => new Map(dealerRows(ix).map((d) => [d.id, d])), [ix]);

  const region = useRegionEditor(null, ix);
  const dealer = useDealerEditor(null, ix);
  const workshop = useWorkshopEditor(null, ix);

  if (isLoading) return <OrgLoading />;
  if (isError) return <OrgError error={error} onRetry={refetch} />;

  const canRegion = access.regions.edit;
  const canDealer = access.dealers.edit;
  const canWorkshop = access.workshops.edit;
  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const toggle = (id) =>
    setCollapsed((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const counts = [
    see.regions && plural(ix.regions.length, "region", "regions"),
    (see.dealers || see.workshops) && plural(ix.dealers.length, "dealer", "dealers"),
    see.workshops && plural(ix.workshops.length, "workshop", "workshops"),
  ].filter(Boolean);
  const nothing = !ix.regions.length && !ix.dealers.length && !ix.workshops.length;
  const filtered = filters.query || filters.region !== "all" || filters.dealer !== "all" || filters.status !== "all";

  // Each sheet hides while its delete is being confirmed, so Escape closes
  // one thing at a time.
  const deleteAndClose = (editor, close) => async () => {
    if (await editor.confirmDelete()) close();
  };

  const shopChip = (w) => (
    <button
      key={w.id}
      type="button"
      onClick={() => setOpenShop(w.id)}
      data-testid="workshop-row"
      className="flex cursor-pointer items-center gap-[10px] rounded-xl border-0 bg-msd-soft px-3 py-[10px] text-left text-inherit hover:bg-msd-surface hover:shadow-msd-e2"
    >
      <Dot color={w.status === "paused" ? "#8E8E93" : "#17B26A"} />
      <span className="min-w-0 grow">
        <b className="block truncate text-[14.5px] font-semibold">{w.location}</b>
        <span className="block text-[13px] text-msd-muted">
          {w.users ? plural(w.users, "person", "people") : "No team yet"}
          {w.status === "paused" ? " · paused" : ""}
        </span>
      </span>
      {w.unfilled ? (
        <span title={`${plural(w.unfilled, "role", "roles")} not filled`} className="flex text-[#F79009]">
          <Icon name="warn" size={15} stroke={2.2} />
        </span>
      ) : (
        <span title="Every role filled" className="flex text-[#17B26A]">
          <Icon name="circlecheck" size={15} stroke={2.2} />
        </span>
      )}
    </button>
  );

  const dealerRow = (d) => (
    <div
      key={d.id ?? "_loose"}
      className="flex flex-col gap-3 rounded-2xl bg-msd-surface p-[14px] shadow-msd-e1"
      data-testid="dealer-row"
    >
      <div className="flex flex-wrap items-center gap-3">
        <Gico icon="bld" size={36} radius={10} iconSize={16} bg={d.id == null ? "rgba(120,120,128,.2)" : TINT} fg={d.id == null ? "var(--muted)" : "var(--royal)"} />
        <div className="min-w-0 grow">
          <b className="block text-[15.5px] font-semibold">{d.name}</b>
          <span className="block text-[13px] text-msd-muted">
            {[
              see.dealers && d.code,
              see.workshops && plural(d.workshopCount, "workshop", "workshops"),
              d.otherRegions.length ? `Also in ${d.otherRegions.join(", ")}` : "",
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </div>
        {d.id != null && canDealer && (
          <Btn size="sm" onClick={() => dealer.openEdit(dealerRowById.get(d.id))} aria-label={`Edit ${d.name}`}>
            Edit
          </Btn>
        )}
        {d.id != null && canWorkshop && (
          <Btn size="sm" icon="plus2" onClick={() => workshop.openAdd(d.id)} aria-label={`Add workshop to ${d.name}`}>
            Workshop
          </Btn>
        )}
      </div>
      {see.workshops && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-2">
          {d.workshops.length ? (
            d.workshops.map(shopChip)
          ) : (
            <div className="px-[2px] py-[6px] text-[13px] text-msd-muted">No workshops yet</div>
          )}
        </div>
      )}
    </div>
  );

  const regionCard = (r) => {
    const open = !collapsed.has(r.id);
    return (
      <section key={r.id} className={cx(CARD, "overflow-hidden")} data-testid="region-row">
        <div className="flex flex-wrap items-center gap-[18px] px-5 py-[18px]">
          <button
            type="button"
            onClick={() => toggle(r.id)}
            aria-expanded={open}
            className={cx(
              "flex min-w-0 flex-[1_1_240px] items-center gap-[14px] border-0 bg-transparent p-0 text-left text-inherit",
              r.none && "opacity-[.85]",
            )}
          >
            <Gico
              icon="map"
              size={44}
              radius={12}
              iconSize={20}
              bg={r.none ? "rgba(120,120,128,.2)" : TINT}
              fg={r.none ? "var(--muted)" : "var(--royal)"}
            />
            <span className="min-w-0">
              <span className={cx("block text-[22px] font-bold", DISP)}>{r.name}</span>
              <span className="text-[13px] text-msd-muted">
                {r.none ? "Dealers here aren't grouped yet" : "Region"}
              </span>
            </span>
            <span className="flex text-msd-faint">
              <Icon name={open ? "up" : "down"} size={18} stroke={2.2} />
            </span>
          </button>
          <div className="flex flex-wrap gap-[22px]">
            {see.dealers && <Stat n={r.stats.dealers} label={r.stats.dealers === 1 ? "dealer" : "dealers"} />}
            <Stat n={r.stats.workshops} label={r.stats.workshops === 1 ? "workshop" : "workshops"} />
            <Stat n={r.stats.people} label="people" />
          </div>
          {!r.none && canRegion && (
            <Btn size="sm" onClick={() => region.openEdit({ id: r.id, name: r.name })} aria-label={`Edit ${r.name}`}>
              Edit
            </Btn>
          )}
        </div>
        <Collapse open={open}>
          <div className="flex flex-col gap-3 bg-msd-ground p-[14px] shadow-[inset_0_1px_0_var(--line)]">
            {r.dealers.length ? (
              r.dealers.map(dealerRow)
            ) : (
              <div className="p-[14px] text-[13px] text-msd-muted">
                {filtered ? "Nothing here matches." : "No dealers in this region yet."}
                {!filtered && canDealer ? " Edit a dealer to place it here." : ""}
              </div>
            )}
            {r.none && canDealer && ix.regions.length > 0 && (
              <div className="px-1 pt-[10px] text-[13px] text-msd-muted">Edit a dealer to move it into a region.</div>
            )}
          </div>
        </Collapse>
      </section>
    );
  };

  const regionOptions = [["all", "All"], ...all.map((g) => [String(g.id), g.name])];

  return (
    <Page>
      <PageHead title="Organization" sub={counts.join(" · ")}>
        {canRegion && (
          <Btn icon="plus2" onClick={region.openAdd}>
            Region
          </Btn>
        )}
        {/* Any dealer, including one with no workshop to show yet. */}
        {canWorkshop && ix.dealers.length > 0 && (
          <Btn icon="plus2" onClick={() => workshop.openAdd()}>
            Workshop
          </Btn>
        )}
        {canDealer && (
          <Btn variant="pri" icon="plus2" onClick={dealer.openAdd}>
            Dealer
          </Btn>
        )}
      </PageHead>

      {!nothing && (
        <div className="flex flex-wrap items-center gap-[10px]">
          <SearchBox
            value={filters.query}
            onChange={(v) => set({ query: v })}
            placeholder="Search"
            aria-label="Search regions, dealers and workshops"
            testid="org-search"
          />
          {(see.dealers || see.workshops) && ix.dealers.length > 0 && (
            <SelectBox value={filters.dealer} onChange={(v) => set({ dealer: v })} label="Dealer">
              <option value="all">Any dealer</option>
              {ix.dealers.map((d) => (
                <option key={d.id} value={String(d.id)}>
                  {d.name}
                </option>
              ))}
            </SelectBox>
          )}
          {see.workshops && (
            <SelectBox value={filters.status} onChange={(v) => set({ status: v })} label="Status">
              <option value="all">Any status</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </SelectBox>
          )}
        </div>
      )}
      {regionOptions.length > 2 && (
        <div className="self-start overflow-x-auto max-w-full">
          <Seg2 options={regionOptions} value={filters.region} onChange={(v) => set({ region: v })} label="Region" />
        </div>
      )}

      {nothing ? (
        <Empty title="No regions, dealers or workshops yet">
          {canRegion || canDealer ? "Add a region and a dealer, then the dealer's workshops." : "Nothing has been set up here yet."}
        </Empty>
      ) : tree.length === 0 ? (
        <Empty title="Nothing matches those filters">Clear a filter, or add what should be here.</Empty>
      ) : (
        tree.map(regionCard)
      )}

      {/* Region */}
      <Sheet
        open={region.open && !region.deleting}
        title={region.editing ? "Edit region" : "Add region"}
        onClose={() => region.setOpen(false)}
        onSubmit={region.submit}
        testid="region-sheet"
        footer={
          <>
            {region.editing && <RemoveBtn onClick={() => region.askDelete(region.editing.id)}>Delete</RemoveBtn>}
            <Btn onClick={() => region.setOpen(false)}>Cancel</Btn>
            <Btn type="submit" variant="pri" disabled={region.pending}>
              {region.editing ? "Save" : "Add region"}
            </Btn>
          </>
        }
      >
        <Field
          label="Region name"
          error={region.errors.name}
          hint="Dealers are placed in a region from each dealer's Edit."
        >
          <input
            type="text"
            className={INPUT}
            value={region.name}
            placeholder="West"
            autoFocus
            onChange={(e) => region.setName(e.target.value)}
          />
        </Field>
      </Sheet>
      <ConfirmDelete
        open={!!region.deleting}
        onClose={() => region.setDeleting(null)}
        name={region.deleting?.name || ""}
        body="This cannot be undone."
        blocked={region.deleting?.blocked}
        onConfirm={deleteAndClose(region, () => region.setOpen(false))}
        pending={region.pending}
      />

      {/* Dealer */}
      <Sheet
        open={dealer.open && !dealer.deleting}
        title={dealer.editing ? "Edit dealer" : "Add dealer"}
        onClose={() => dealer.setOpen(false)}
        onSubmit={dealer.submit}
        testid="dealer-sheet"
        footer={
          <>
            {dealer.editing && <RemoveBtn onClick={() => dealer.askDelete(dealer.editing.id)}>Delete</RemoveBtn>}
            <Btn onClick={() => dealer.setOpen(false)}>Cancel</Btn>
            <Btn type="submit" variant="pri" disabled={dealer.pending}>
              {dealer.editing ? "Save" : "Add dealer"}
            </Btn>
          </>
        }
      >
        <Field label="Dealer name" error={dealer.errors.name}>
          <input
            type="text"
            className={INPUT}
            value={dealer.form.name}
            placeholder="Kataria Motors"
            autoFocus
            onChange={(e) => dealer.setForm({ ...dealer.form, name: e.target.value })}
          />
        </Field>
        <Field label="Dealer code" error={dealer.errors.dealer_code}>
          <input
            type="text"
            className={INPUT}
            value={dealer.form.dealer_code}
            placeholder="KM-01"
            onChange={(e) => dealer.setForm({ ...dealer.form, dealer_code: e.target.value })}
          />
        </Field>
        <FieldGroup
          label="Regions"
          hint={
            ix.regions.length
              ? "A dealer can serve more than one region."
              : "No regions yet. A dealer can be added without one and placed later."
          }
        >
          {ix.regions.length > 0 && (
            <div className={TG_LIST}>
              {ix.regions.map((r) => (
                <div key={r.id} className={TG_ROW}>
                  <Gico icon="map" bg={TINT} fg="var(--royal)" />
                  <b className="grow text-[15px] font-semibold">{r.name}</b>
                  <Switch
                    on={dealer.form.region_ids.includes(r.id)}
                    onChange={() => dealer.toggleRegion(r.id)}
                    label={r.name}
                  />
                </div>
              ))}
            </div>
          )}
        </FieldGroup>
      </Sheet>
      <ConfirmDelete
        open={!!dealer.deleting}
        onClose={() => dealer.setDeleting(null)}
        name={dealer.deleting?.name || ""}
        body="This cannot be undone."
        blocked={dealer.deleting?.blocked}
        onConfirm={deleteAndClose(dealer, () => dealer.setOpen(false))}
        pending={dealer.pending}
      />

      {/* Adding a workshop */}
      <Sheet
        open={workshop.adding}
        title="Add workshop"
        onClose={() => workshop.setAdding(false)}
        onSubmit={workshop.submitAdd}
        testid="workshop-add-sheet"
        footer={
          <>
            <Btn onClick={() => workshop.setAdding(false)}>Cancel</Btn>
            <Btn type="submit" variant="pri" disabled={workshop.pending}>
              Add workshop
            </Btn>
          </>
        }
      >
        <Field label="Workshop name" error={workshop.errors.workshop_location}>
          <input
            type="text"
            className={INPUT}
            value={workshop.form.workshop_location}
            placeholder="Bandra"
            autoFocus
            onChange={(e) => workshop.setForm({ ...workshop.form, workshop_location: e.target.value })}
          />
        </Field>
        <Field label="Dealer" error={workshop.errors.dealer_id}>
          <select
            className={INPUT}
            value={workshop.form.dealer_id}
            onChange={(e) => workshop.setForm({ ...workshop.form, dealer_id: e.target.value })}
          >
            {ix.dealers.map((d) => {
              const regions = regionNamesForDealer(d, ix);
              return (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {regions.length ? ` · ${regions.join(", ")}` : ""}
                </option>
              );
            })}
          </select>
        </Field>
      </Sheet>

      {/* One workshop: its team, its status, deleting it */}
      <WorkshopSheet
        ix={ix}
        workshopId={openShop}
        onClose={() => setOpenShop(null)}
        editor={workshop}
        canEdit={canWorkshop}
        canEditPeople={access.people.edit}
      />
    </Page>
  );
}
